// components/TestRunner/rules/Rule03_Measurement_FreeOnly.js
import { RuleUtils } from "../../../utils/RuleUtils.js";
/**
 * Rule03_Measurement_FreeOnly.js
 *
 * ✅ Règle 03 — Une fibre libre doit avoir ses données de mesures complètes selon sa catégorie PV
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Vérification de l'existence : fiber.etat, objets pvReussite, pvEchec, pvHs
 * • Contrôle RuleUtils.isFiberFree(fiber)
 * • Validation complétude Réussite : hasDcoPvReussite, 2 fichiers, nm1310, nm1550
 * • Validation complétude Échec : hasDcoPvEchec, ≥2 fichiers pairs, nm1310, nm1550
 * • Validation complétude Hors Service : 2 fichiers, nm1310, nm1550 (pas de DCO)
 */

export class Rule03_Measurement_FreeOnly {
  /**
   * Appliquer la règle 03 : Mesures complètes pour fibres libres
   * @param {Object} fiber - Objet fibre avec toutes ses données
   */
  static apply(fiber) {
    // 1. Vérifier SEULEMENT l'état (pas les PV)
    if (!fiber.etat) {
      this._addError(
        fiber,
        "R03",
        "Impossible d'exécuter le test - État de la fibre manquant",
        "Vérifier la déclaration de la fibre dans la Configuration des Tiroirs"
      );
      return;
    }

    // 2. Vérifier si libre
    if (!RuleUtils.isFiberFree(fiber)) {
      return; // Ignore les fibres non-libres
    }

    // 3. Maintenant vérifier les PV (seulement pour fibres libres)
    const missingObjectError = this._checkMissingObjects(fiber);
    if (missingObjectError) {
      this._addError(fiber, "R03", missingObjectError.title, missingObjectError.message);
      return;
    }

    // 4. Détecter la catégorie active
    const category = this._detectActiveCategory(fiber);
    if (!category) {
      return; // Rule01 gère ce cas
    }

    // 5. Valider la complétude selon la catégorie
    const completenessError = this._validateCompleteness(fiber, category);
    if (completenessError) {
      this._addError(fiber, "R03", completenessError.title, completenessError.message);
    }
  }

  /**
   * Vérifier l'existence des objets PV nécessaires
   * @param {Object} fiber - Objet fibre
   * @returns {Object|null} Erreur ou null si OK
   */
  static _checkMissingObjects(fiber) {
    // Vérifier qu'au moins UN objet PV existe (pas tous !)
    const hasPvReussite = fiber.pvReussite !== undefined && fiber.pvReussite !== null;
    const hasPvEchec = fiber.pvEchec !== undefined && fiber.pvEchec !== null;
    const hasPvHs = fiber.pvHs !== undefined && fiber.pvHs !== null;

    if (!hasPvReussite && !hasPvEchec && !hasPvHs) {
      return {
        title: "Aucune mesure SOR disponible",
        message:
          "Cette fibre ne dispose d'aucun fichier de mesure SOR - aucune donnée de type Succès, Échec ou Hors Service n'a été trouvée",
      };
    }

    return null; // OK si état + au moins un PV
  }

  /**
   * Détecter la catégorie active
   * @param {Object} fiber - Objet fibre
   * @returns {string|null} 'reussite', 'echec', 'hs', ou null
   */
  static _detectActiveCategory(fiber) {
    if (fiber.hasPvHs === true) return "hs";
    if (fiber.hasPvReussite === true) return "reussite";
    if (fiber.hasPvEchec === true) return "echec";
    return null;
  }

  /**
   * Valider la complétude d'une catégorie
   * @param {Object} fiber - Objet fibre
   * @param {string} category - Catégorie active
   * @returns {Object|null} Erreur ou null si OK
   */
  static _validateCompleteness(fiber, category) {
    switch (category) {
      case "reussite":
        return this._validateReussiteCompleteness(fiber);
      case "echec":
        return this._validateEchecCompleteness(fiber);
      case "hs":
        return this._validateHsCompleteness(fiber);
      default:
        return null;
    }
  }

  /**
   * Valider la complétude Réussite
   * @param {Object} fiber - Objet fibre
   * @returns {Object|null} Erreur ou null si OK
   */
  static _validateReussiteCompleteness(fiber) {
    const filesCount = this._getFilesCount(fiber.pvReussite);
    const hasDco = fiber.hasDcoPvReussite === true;
    const hasNm1310 = fiber.pvReussite?.nm1310 === true;
    const hasNm1550 = fiber.pvReussite?.nm1550 === true;
    const hasCorrectFileCount = filesCount === 2;

    // Si tout est OK, pas d'erreur
    if (hasDco && hasCorrectFileCount && hasNm1310 && hasNm1550) {
      return null;
    }

    return {
      title: "Configuration Réussite incomplète - Éléments de mesures manquants",
      message: this._generateReussiteCompletenessMessage(fiber, {
        hasDco,
        filesCount,
        hasCorrectFileCount,
        hasNm1310,
        hasNm1550,
      }),
    };
  }

  /**
   * Valider la complétude Échec
   * @param {Object} fiber - Objet fibre
   * @returns {Object|null} Erreur ou null si OK
   */
  static _validateEchecCompleteness(fiber) {
    const filesCount = this._getFilesCount(fiber.pvEchec);
    const hasDco = fiber.hasDcoPvEchec === true;
    const hasNm1310 = fiber.pvEchec?.nm1310 === true;
    const hasNm1550 = fiber.pvEchec?.nm1550 === true;
    const hasCorrectFileCount = filesCount >= 2 && filesCount % 2 === 0;

    // Si tout est OK, pas d'erreur
    if (hasDco && hasCorrectFileCount && hasNm1310 && hasNm1550) {
      return null;
    }

    return {
      title: "Configuration Échec incomplète - Éléments de mesures manquants",
      message: this._generateEchecCompletenessMessage(fiber, {
        hasDco,
        filesCount,
        hasCorrectFileCount,
        hasNm1310,
        hasNm1550,
      }),
    };
  }

  /**
   * Valider la complétude Hors Service
   * @param {Object} fiber - Objet fibre
   * @returns {Object|null} Erreur ou null si OK
   */
  static _validateHsCompleteness(fiber) {
    const filesCount = this._getFilesCount(fiber.pvHs);
    const hasNm1310 = fiber.pvHs?.nm1310 === true;
    const hasNm1550 = fiber.pvHs?.nm1550 === true;
    const hasCorrectFileCount = filesCount === 2;

    // Si tout est OK, pas d'erreur
    if (hasCorrectFileCount && hasNm1310 && hasNm1550) {
      return null;
    }

    return {
      title: "Configuration Hors Service incomplète - Éléments de mesures manquants",
      message: this._generateHsCompletenessMessage(fiber, {
        filesCount,
        hasCorrectFileCount,
        hasNm1310,
        hasNm1550,
      }),
    };
  }

  /**
   * Obtenir le nombre de fichiers d'un objet PV
   * @param {Object} pvObject - Objet PV
   * @returns {number} Nombre de fichiers
   */
  static _getFilesCount(pvObject) {
    if (!pvObject || !pvObject.files || !Array.isArray(pvObject.files)) {
      return 0;
    }
    return pvObject.files.length;
  }

  /**
   * Générer le message pour objets PV manquants
   * @param {Object} fiber - Objet fibre
   * @param {Array} requiredFields - Liste des champs requis
   * @returns {string} Message HTML formaté
   */
  static _generateMissingObjectsMessage(fiber, requiredFields) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Impossible d'exécuter le test - Données de configuration manquantes<br><br>`;

    message += `<strong>Détail des flags manquants :</strong><br>`;
    requiredFields.forEach((item) => {
      const value = fiber[item.field];
      const status = value !== undefined && value !== null ? "✅" : "❌";
      message += `- ${item.label} : ${status}<br>`;
    });

    message += `<br><strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la déclaration de la fibre dans la Configuration des Tiroirs<br>`;
    message += `- Vérifier la présence dans un rapport fiber cable<br>`;
    message += `- Vérifier la présence de fichiers de mesures PV Réussite, PV Échec, PV Hors Service`;

    return message;
  }

  /**
   * Générer le message pour complétude Réussite
   * @param {Object} fiber - Objet fibre
   * @param {Object} validation - Résultats de validation
   * @returns {string} Message HTML formaté
   */
  static _generateReussiteCompletenessMessage(fiber, validation) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Configuration Réussite incomplète - Éléments de mesures manquants<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Succès : ${validation.hasDco ? "✅" : "❌"}<br>`;
    message += `- Nombre de fichiers attendu (2) : ${validation.hasCorrectFileCount ? "✅" : "❌"} (actuel: ${validation.filesCount})<br>`;
    message += `- Mesure nm1310 présente : ${validation.hasNm1310 ? "✅" : "❌"}<br>`;
    message += `- Mesure nm1550 présente : ${validation.hasNm1550 ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence d'une mesure avec un PV de réussite`;

    return message;
  }

  /**
   * Générer le message pour complétude Échec
   * @param {Object} fiber - Objet fibre
   * @param {Object} validation - Résultats de validation
   * @returns {string} Message HTML formaté
   */
  static _generateEchecCompletenessMessage(fiber, validation) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Configuration Échec incomplète - Éléments de mesures manquants<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Échec : ${validation.hasDco ? "✅" : "❌"}<br>`;
    message += `- Nombre de fichiers attendu (≥2 et pair) : ${validation.hasCorrectFileCount ? "✅" : "❌"} (actuel: ${validation.filesCount})<br>`;
    message += `- Mesure nm1310 présente : ${validation.hasNm1310 ? "✅" : "❌"}<br>`;
    message += `- Mesure nm1550 présente : ${validation.hasNm1550 ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence d'une mesure avec un PV d'échec`;

    return message;
  }

  /**
   * Générer le message pour complétude Hors Service
   * @param {Object} fiber - Objet fibre
   * @param {Object} validation - Résultats de validation
   * @returns {string} Message HTML formaté
   */
  static _generateHsCompletenessMessage(fiber, validation) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Configuration Hors Service incomplète - Éléments de mesures manquants<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Nombre de fichiers attendu (2) : ${validation.hasCorrectFileCount ? "✅" : "❌"} (actuel: ${validation.filesCount})<br>`;
    message += `- Mesure nm1310 présente : ${validation.hasNm1310 ? "✅" : "❌"}<br>`;
    message += `- Mesure nm1550 présente : ${validation.hasNm1550 ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence d'une mesure avec un PV hors service`;

    return message;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
